import React, { useEffect, useRef, useState } from 'react';
import { useScheduleActionLock } from '../../../hook/useScheduleActionLock';
import { Col, concatDateAndTimeDayjs, isSameDate, Row } from '../../../lib/utils';
import { Subtitle1Bold } from '../../../lib/font';
import { colors } from '../../../lib/colors';
import WhiteSquareButton from '../../Button/WhiteSquareButton';
import ColorSquareButton from '../../Button/ColorSquareButton';
import SvgIcon from '../../SvgIcon';
import dayjs, { Dayjs } from 'dayjs';
import Calendar from '../../Calendar/Calendar';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createOneOnOneScheduleAPI, createOneOnTwoScheduleAPI, getMemberListForCenterAPI } from '../../../api/schedule';
import CreateOneScheduleLeft from './CreateOneScheduleLeft';
import CreateOneScheduleRight from './CreateOneScheduleRight';
import _, { replace } from 'lodash';
import { useNavigate } from 'react-router-dom';
import ModalMembershipCreate from '../../MemberProfile/ModalMembershipCreate';
import ModalRegisterPass from '../../../scenes/ClassPass/ModalRegisterPass/ModalRegisterPass';
import {
    buildWritableScheduleMemberOptions,
    isScheduleMemberPickerReadOnly,
    refreshSelectedScheduleMembers,
} from '../memberPicker';
import {
    getSharedScheduleErrorMessage,
    getDuetFailureFollowUpNotice,
    isResubmitBlocked,
    MUST_REVIEW_SCHEDULE_LIST_NOTICE,
    nextResubmitGuardState,
    ResubmitGuardState,
    getStableSharedScheduleOperation,
    shouldDiscardSharedScheduleOperation,
    StableSharedScheduleOperation,
    useScheduleMembershipEntitlements,
} from '../../../features/sharedMembership';
import { refreshScheduleWriteQueries } from '../scheduleWriteCache';
import {
    buildScheduleConfirmAlertContent,
    resolveScheduleSubmitDecision,
    resolveScheduleSubmitPlan,
} from '../scheduleSubmitPlan';
import useMembershipPauseCreateGate from '../../MembershipPause/useMembershipPauseCreateGate';
import { compactTargets, toPtPauseGateTarget } from '../../../features/membershipPause/createGate';
import {
    findScheduleDateWarnings,
    formatScheduleDateWarningMessage,
    formatSharedDateBlockMessage,
    ScheduleDateWarningItem,
} from '../scheduleDateWarning';
import { dispatchScheduleSubmitDecision } from '../scheduleConfirmAttempt';
import { useScheduleConfirmDialog } from '../scheduleConfirmDialog';

type RefineDate = {
    refineStartTime: string;
    refineEndTime: string;
};

/**
 * 등록 버튼을 누른 순간 한 번 만든 **보낼 요청 내용**.
 *
 * 날짜 경고 판정·확인 창·전송이 모두 이 객체 하나를 쓴다. mutation 안에서 순번 상태로
 * 다시 만들지 않는다 — 창이 떠 있는 동안 목록이 다시 와도 확인한 수강권·시각 그대로 나간다.
 */
type OneOnOneSubmission = {
    target: 'ONE_ON_ONE';
    centerId: string;
    coachId: string;
    coachLabel: string;
    memberId: string;
    membership: any;
    refineDate: RefineDate;
    intentKey: string;
};

type OneOnTwoSubmission = {
    target: 'ONE_ON_TWO';
    centerId: string;
    coachId: string;
    scheduleTimes: Array<{
        memberId: string;
        startTime: string;
        endTime: string;
        membershipId?: string;
        membershipSharePoolId?: string;
    }>;
    sharedMembership: any;
    refineDate: RefineDate;
    intentKey: string;
};

type ScheduleSubmission = OneOnOneSubmission | OneOnTwoSubmission;

const CreateOneSchedule = ({
    coachOption,
    authorization,
    authObject,
    createAlert,
    selectedSchedule,
    calendarType,
    calendarSelectedDay,
    calendarScrollY,
    returnPath,
    returnState,
    sharedMembershipStaffBookingEnabled,
}: any) => {
    /** 스케줄 동작 잠금(생성) — 진입은 경로 가드가, 저장 직전 재검사(30분 만료)는 여기서 한다 */
    const scheduleLock = useScheduleActionLock('schedule.create');
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const submissionLockRef = useRef(false);
    const sharedOperationRef = useRef<StableSharedScheduleOperation | null>(null);
    /**
     * 1:2 등록 화면이 더 제출해도 되는 상태인가.
     *
     * 일정이 남아 있을 수 있으면(partialWriteMayExist) 직원이 목록부터 보게 한다.
     * 입력을 보지 않고 **화면 단위로** 닫는다 — 부분 성공에서 어느 회원이 살아남았는지
     * 모르므로 "다른 입력이니 안전하다"고 볼 근거가 없다.
     *
     * ⚠️ **중복을 막는 것은 이 잠금이 아니라 서버다.** 이 ref 는 화면 수명에 묶여 있어
     * 이동·새로고침으로 사라지고 다른 탭에는 없다. 그래도 괜찮은 이유는, 같은 회원·같은 시각의
     * 활성 일정이 이미 있으면 서버가 409 SHARED_MEMBERSHIP_DUPLICATE_SCHEDULE 로 요청 전체를
     * 거절하기 때문이다. 여기서 하는 일은 "빨리 알리는 것"이다.
     */
    const resubmitGuardRef = useRef<ResubmitGuardState>('OPEN');
    /**
     * 등록 직전 확인 창은 공통 알럿이 아니라 이 화면의 지역 모달로 띄운다. 공통 알럿은 늦게 온 다른
     * 알럿이 앞 확인 콜백을 물려받아 수업을 보낼 수 있다(scheduleConfirmDialog 주석).
     */
    const { openConfirm, closeConfirm, confirmDialog } = useScheduleConfirmDialog();
    // 수강권 일시정지 — 정지 기간 안 생성이면 「풀고 등록 / 그대로 등록 / 취소」(P1·P2)
    const pauseGate = useMembershipPauseCreateGate({ createAlert });
    /** 생성 실패 안내. 「풀고 등록」 뒤 실패면 정지가 풀렸다는 문구를 앞에 붙인다. */
    const alertCreateFailure = (title: string, message: string, ...rest: any[]) =>
        createAlert(title, pauseGate.decorateFailureMessage(message), ...rest);
    const currentTime = selectedSchedule ? dayjs(selectedSchedule?.scheduleStartTime) : dayjs();
    const roundedTime = currentTime.minute(Math.floor(currentTime.minute() / 10) * 10).second(0);
    const currentTimeAdd50 = dayjs(roundedTime).add(50, 'minute');
    const _memberId = selectedSchedule ? selectedSchedule?.memberId : '';
    const _membershipId = selectedSchedule ? selectedSchedule?.membershipId : '';

    /**
     * coach : 담당강사
     * startTime : 시작 시간
     * endTime : 종료시간
     * isOneOnOneClass : true - 1:1수업 , false - 2:1수업
     * isDisabledInput : 1:1수업 또는 1:2 수업 클릭시 멤버가 다선택되면 input 비활성화
     */
    const [coach, setCoach] = useState({
        value: selectedSchedule ? selectedSchedule?.coachId : '',
        label: selectedSchedule ? selectedSchedule?.coachName : '강사를 선택해주세요!',
    });
    const [startTime, setStartTime] = useState({ value: roundedTime, label: dayjs(roundedTime).format('A hh:mm') });
    const [endTime, setEndTime] = useState({
        value: currentTimeAdd50,
        label: dayjs(currentTimeAdd50).format('A hh:mm'),
    });
    const [isOneOnOneClass, setIsOneOnOneClass] = useState<boolean>(true);
    const [isDisabledInput, setIsDisabledInput] = useState<boolean>(false);

    /**
     * searchText: 회원 검색
     * memberList: 강사의 연결된 모든 회원의 리스트
     * filteredMemberList : 검색결과가 반영된 회원 리스트
     * selectedMember: 선택된 회원
     */
    const [searchText, setSearchText] = useState<string>('');
    const [memberList, setMemberList] = useState<any>([]);
    const [filteredMemberList, setFilteredMemberList] = useState<any>([]);
    const [selectedMember, setSelectedMember] = useState<any>([]);

    const {
        selectedMembers: scheduleSelectedMember,
        excludedSharedMembershipCounts,
        isReady: areMembershipEntitlementsReady,
        isLoading: areMembershipEntitlementsLoading,
        isError: hasMembershipEntitlementsError,
        refetch: refetchMembershipEntitlements,
    } = useScheduleMembershipEntitlements({
        selectedMembers: selectedMember,
        centerId: String(authObject?.centerId || ''),
        coachId: String(coach?.value || ''),
        enabled: sharedMembershipStaffBookingEnabled === true,
    });

    const [oneOnOneMemberShipIndex, setOneOnOneMemberShipIndex] = useState<number>(1);

    const [firstMemberShipIndex, setFirstMemberShipIndex] = useState<number>(1);
    const [secondMemberShipIndex, setSecondMemberShipIndex] = useState<number>(1);

    /** 캘린더 state */
    const [selectedDay, setSelectedDay] = useState<any>(
        selectedSchedule ? new Date(selectedSchedule?.scheduleStartTime) : new Date()
    );
    const [isCalendar, setIsCalendar] = useState<boolean>(false);

    const [timeList, setTimeList] = useState<any>(null); // 10분단위 시간 배열

    // 수강권 등록 모달 열기
    const [isModalMembershipCreateVisible, setIsModalMembershipCreateVisible] = useState<boolean>(false);

    // 회원 수강권 새로 생성 모달
    const [isRegisterPassModal, setIsRegisterPassModal] = useState<boolean>(false);

    // selectedMemberName : 선택된 회원의 이름
    // selectedMemberId : 선택된 회원의 userId
    const [selectedMemberName, setSelectedMemberName] = useState<string>('');
    const [selectedMemberId, setSelectedMemberId] = useState<string>('');

    // 회원 검색 이벤트
    const onChangeText = (e: any) => {
        setSearchText(e.target.value);
    };

    // 담당 강사 선택
    const handleCoachSelect = (option: any) => {
        setCoach(option);
    };

    // 시작 시간 선택
    const handleStartTimeSelect = (option: any) => {
        setStartTime(option);
    };

    // 종료 시간 선택
    const handleEndTimeSelect = (option: any) => {
        setEndTime(option);
    };

    // 멤버 선택
    const handleMemberSelect = (option: any) => {
        if (isScheduleMemberPickerReadOnly(option)) return;
        setSearchText('');
        let cloneSelectedMember = _.cloneDeep(selectedMember);
        cloneSelectedMember.push(option);
        setSelectedMember(cloneSelectedMember);
    };

    // 선택된 회원 지우는 함수
    const onClickDeleteSelectedMember = (value: string) => {
        const filterSelectedMemberList = selectedMember.filter((e: any) => e.value !== value);
        setSelectedMember(filterSelectedMemberList);

        setOneOnOneMemberShipIndex(1);
        setFirstMemberShipIndex(1);
        setSecondMemberShipIndex(1);
    };

    // 수강권 추가하기 버튼 클릭이벤트
    const onClickClassPassPage = (selectedMember: any) => {
        setSelectedMemberId(selectedMember.value);
        setSelectedMemberName(selectedMember.label);
        setIsModalMembershipCreateVisible(true);
    };

    //  회원 리스트 조회 API : (GET)
    const getMemberListForCenter = useQuery(
        ['getMemberListForCenterAPI', coach?.value],
        async () => await getMemberListForCenterAPI(authObject?.centerId, coach?.value, authorization),
        {
            onSuccess: (res) => {
                if (res.status === 200) {
                    const memberListToRes = buildWritableScheduleMemberOptions(
                        res?.data?.memberDataList
                    );
                    setMemberList(memberListToRes);
                    setFilteredMemberList(memberListToRes);
                    setSelectedMember(
                        refreshSelectedScheduleMembers(selectedMember, memberListToRes)
                    );
                }
            },
            onError: (err) => {
                console.log(err);
            },
            enabled: !!authorization && !!coach?.value,
            // refetchOnWindowFocus: 'always',
            staleTime: 0,
        }
    );

    // 1:1수업 생성 API : (POST)
    /** 1:1 요청 내용. 의도 키도 여기서 같이 굳힌다(규칙은 예전과 같다). */
    const buildOneOnOneSubmission = (refineDate: RefineDate): OneOnOneSubmission => {
        const membership = scheduleSelectedMember[0]?.memberships[oneOnOneMemberShipIndex - 1];
        const centerId = authObject?.centerId;
        const coachId = coach?.value;
        const memberId = scheduleSelectedMember[0]?.value;
        const intentKey = JSON.stringify({
            kind: 'ONE_ON_ONE',
            centerId: String(centerId || ''),
            coachId: String(coachId || ''),
            memberId: String(memberId || ''),
            membershipId: String(membership?.id || membership?._id || ''),
            membershipSharePoolId: String(membership?.membershipSharePoolId || ''),
            startTime: refineDate?.refineStartTime,
            endTime: refineDate?.refineEndTime,
        });
        return {
            target: 'ONE_ON_ONE',
            centerId,
            coachId,
            coachLabel: coach?.label,
            memberId,
            membership,
            refineDate,
            intentKey,
        };
    };

    const createOneOnOneSchedule = useMutation({
        mutationFn: async (submission: OneOnOneSubmission) => {
            const { membership, refineDate } = submission;
            const sharedOperation = getStableSharedScheduleOperation(
                sharedOperationRef.current,
                submission.intentKey,
                membership,
            );
            sharedOperationRef.current = sharedOperation ?? null;
            return await createOneOnOneScheduleAPI(
                submission.centerId,
                submission.coachId,
                submission.memberId,
                refineDate?.refineStartTime,
                refineDate?.refineEndTime,
                membership?.id,
                authorization,
                sharedOperation?.writeContext,
            );
        },
        onSuccess: (res, { refineDate, coachLabel }) => {
            const sharedErrorMessage = getSharedScheduleErrorMessage(res);
            if (res.status === 201 || res.status === 200) {
                sharedOperationRef.current = null;
                // 스케줄 생성은 수강권 잔여회차를 바꾸므로 회원 상세 수강권 쿼리 무효화
                queryClient.invalidateQueries({ queryKey: ['getMembershipForMemberQuery'] });
                queryClient.invalidateQueries({ queryKey: ['getAllMembershipForMemberQuery'] });
                queryClient.invalidateQueries({ queryKey: ['sharedMembership', 'staffEntitlements'] });
                queryClient.invalidateQueries({ queryKey: ['getSmartRangeSchedulesAPI'] });
                queryClient.invalidateQueries({ queryKey: ['getListRangeSchedulesAPI'] });
                const startTime = refineDate.refineStartTime;
                navigate('/schedule', {
                    state: { calendarType, calendarTimeInfo: startTime },
                    replace: true,
                });
            } else if (sharedErrorMessage) {
                if (shouldDiscardSharedScheduleOperation(res)) {
                    sharedOperationRef.current = null;
                }
                queryClient.invalidateQueries({ queryKey: ['sharedMembership', 'staffEntitlements'] });
                alertCreateFailure('공유 수강권 확인', sharedErrorMessage, true);
            } else if (res.status === 410 || res.status === 420 || res.status === 430) {
                alertCreateFailure(
                    '중복',
                    `선택된 담당 강사 ${coachLabel}님이\n해당시간에 중복된 스케줄이 존재합니다`,
                    true
                );
            } else {
                alertCreateFailure('오류', `서버 내부 오류\n 지속적인 오류발생시 고객센터로 문의바랍니다`, true);
            }
        },
        onError: () => {
            alertCreateFailure('오류', '일정 생성 요청에 실패했습니다\n네트워크 상태를 확인한 뒤 다시 시도해주세요', true);
        },
        onSettled: () => {
            submissionLockRef.current = false;
        },
    });

    /**
     * 1:2 요청 payload와 그 의도를 나타내는 intentKey를 함께 만든다.
     *
     * 제출 전 중복 차단(duplicateRiskIntentKeyRef)이 뮤테이션 밖에서 같은 키를 비교해야 하므로
     * 한곳에서만 만든다. 두 곳에서 따로 만들면 규칙이 갈리는 순간 차단이 조용히 새거나
     * 엉뚱한 입력을 막는다.
     */
    const buildOneOnTwoSubmission = (refineDate: RefineDate): OneOnTwoSubmission => {
        const memberships = [
            scheduleSelectedMember[0]?.memberships[firstMemberShipIndex - 1],
            scheduleSelectedMember[1]?.memberships[secondMemberShipIndex - 1],
        ];
        const scheduleTimes = memberships.map((membership, index) => ({
            memberId: selectedMember[index]?.value,
            startTime: refineDate?.refineStartTime,
            endTime: refineDate?.refineEndTime,
            membershipId: membership?.id,
            ...(membership?.membershipSharePoolId && {
                membershipSharePoolId: membership.membershipSharePoolId,
            }),
        }));

        /**
         * 공유 회차가 섞이면 서버가 풀 단위로 두 회차를 한꺼번에 확보한다.
         * 재시도가 중복 차감되지 않도록 같은 의도에는 같은 operationId를 재사용한다.
         * 공유 수강권이 하나도 없으면 컨텍스트가 만들어지지 않아 예전 경로 그대로 나간다.
         */
        const sharedMembership = memberships.find(
            (membership) => membership?.membershipSharePoolId,
        );
        const intentKey = JSON.stringify({
            kind: 'ONE_ON_TWO',
            centerId: String(authObject?.centerId || ''),
            coachId: String(coach?.value || ''),
            startTime: refineDate?.refineStartTime,
            endTime: refineDate?.refineEndTime,
            items: scheduleTimes.map((item) => ({
                memberId: String(item.memberId || ''),
                membershipId: String(item.membershipId || ''),
            })),
        });

        return {
            target: 'ONE_ON_TWO',
            centerId: authObject?.centerId,
            coachId: coach?.value,
            scheduleTimes,
            sharedMembership,
            refineDate,
            intentKey,
        };
    };

    // 1:2수업 생성 API : (POST)
    const createOneOnTwoSchedule = useMutation({
        mutationFn: async (submission: OneOnTwoSubmission) => {
            const { scheduleTimes, sharedMembership, intentKey } = submission;
            const sharedOperation = getStableSharedScheduleOperation(
                sharedOperationRef.current,
                intentKey,
                sharedMembership,
            );
            sharedOperationRef.current = sharedOperation ?? null;

            return await createOneOnTwoScheduleAPI(
                submission.centerId,
                submission.coachId,
                scheduleTimes,
                authorization,
                sharedOperation?.writeContext,
            );
        },
        onSuccess: (res, { refineDate }) => {
            // 1:2는 실패해도 앞서 만들어진 일정을 되돌리지 않는다. 성공이든 실패든
            // 서버 상태가 바뀌었을 수 있으므로 목록을 캐시 유무와 무관하게 다시 받는다.
            refreshScheduleWriteQueries(queryClient, {
                centerId: authObject?.centerId,
                authorization,
                date: dayjs(refineDate?.refineStartTime).format('YYYY-MM-DD'),
            });

            if (res.status === 201) {
                sharedOperationRef.current = null;
                const startTime = refineDate.refineStartTime;
                navigate('/schedule', {
                    state: { calendarType, calendarTimeInfo: startTime },
                    replace: true,
                });
                return;
            }

            // 키 폐기는 retryPolicy 로만 판정한다(계약 §2-2). 부분 성공 가능성은 별개 질문이다.
            if (shouldDiscardSharedScheduleOperation(res)) {
                sharedOperationRef.current = null;
            }
            // 일정이 남아 있을 수 있으면(partialWriteMayExist) 목록부터 보게 한다.
            // 키를 버렸는지(retryPolicy)는 여기서 보지 않는다 — 다른 질문이다(계약 §2-4).
            // 중복의 실제 방어는 서버의 SHARED_MEMBERSHIP_DUPLICATE_SCHEDULE 이고,
            // 이 잠금은 직원이 상황을 빨리 알게 하는 안내다.
            resubmitGuardRef.current = nextResubmitGuardState(resubmitGuardRef.current, res);

            // 중복 일정(410·420·430) 분기는 두지 않는다. 이 엔드포인트(duplicable)는 그 코드를
            // 내지 않는다 — 컨트롤러가 preCheck·isDuplicateTimes 를 부르지 않고, 개인 항목이 타는
            // addScheduleV2 에도 해당 throw 가 없다(420 을 던지는 checkSchedulesForBlock 은
            // 휴무 블록 전용이라 이 경로와 무관하다).
            const sharedErrorMessage = getSharedScheduleErrorMessage(res);
            const causeTitle = sharedErrorMessage ? '공유 수강권 확인' : '오류';
            const causeMessage =
                sharedErrorMessage ??
                (res.status === 0
                    ? // 응답을 못 받았다. 서버는 계속 처리했을 수 있다.
                      '일정 생성 요청에 실패했습니다\n네트워크 상태를 확인한 뒤 다시 시도해주세요'
                    : '서버 내부 오류\n지속적인 오류발생시 고객센터로 문의바랍니다');

            // 위험 상태면 원인과 함께 목록으로 보낸다. 알럿은 하나뿐이고 '계속 등록' 같은
            // 우회 선택지를 두지 않는다 — 그게 남아 있으면 같은 구멍이 그대로다.
            if (isResubmitBlocked(resubmitGuardRef.current)) {
                alertCreateFailure(
                    causeTitle,
                    `${causeMessage}\n\n${MUST_REVIEW_SCHEDULE_LIST_NOTICE}`,
                    true,
                    '목록 확인',
                    () => goToScheduleList(refineDate.refineStartTime),
                );
                return;
            }

            // 원인 문구가 먼저, 후속 안내가 그 뒤에 붙는다. 알럿은 하나만 띄운다.
            // 푸시·챗봇 실패처럼 부분 성공 필드가 아예 없는 실패도 목록 확인을 안내한다(계약 §1-4).
            const followUpNotice = getDuetFailureFollowUpNotice(res, {
                resolveMemberName: (memberId: string) =>
                    selectedMember.find((member: any) => member?.value === memberId)?.label || '',
                omitIfRedundantWith: causeMessage,
            });
            alertCreateFailure(
                causeTitle,
                followUpNotice ? `${causeMessage}\n\n${followUpNotice}` : causeMessage,
                true,
            );
        },
        onError: (_error, { refineDate }) => {
            refreshScheduleWriteQueries(queryClient, {
                centerId: authObject?.centerId,
                authorization,
                date: dayjs(refineDate?.refineStartTime).format('YYYY-MM-DD'),
            });
            alertCreateFailure(
                '오류',
                '일정 생성 요청에 실패했습니다\n네트워크 상태를 확인한 뒤 다시 시도해주세요\n\n일정이 이미 만들어졌을 수 있습니다\n일정 목록에서 확인해주세요.',
                true,
            );
        },
        onSettled: () => {
            submissionLockRef.current = false;
        },
    });

    /** 일정 목록으로 보낸다. 성공 경로와 같은 이동 방식이라 도착 화면·날짜가 동일하다. */
    const goToScheduleList = (startTime: string) =>
        navigate('/schedule', {
            state: { calendarType, calendarTimeInfo: startTime },
            replace: true,
        });

    const submitOneOnOneSchedule = (submission: OneOnOneSubmission) => {
        if (submissionLockRef.current) return;
        submissionLockRef.current = true;
        createOneOnOneSchedule.mutate(submission);
    };

    const submitOneOnTwoSchedule = (submission: OneOnTwoSubmission) => {
        if (submissionLockRef.current) return;
        const { refineDate } = submission;

        /**
         * 위험 상태면 이 화면에서는 더 제출하지 않고 목록으로 보낸다.
         *
         * onSuccess 의 알럿에서 이미 보냈어야 하지만, 그 알럿을 버튼 대신 다른 방법으로
         * 닫았다면 사용자가 화면에 남는다. 그때 등록을 다시 누르는 경로를 여기서 닫는다.
         *
         * 입력을 비교하지 않는다 — 부분 성공에서 어느 회원이 살아남았는지 모르기 때문이다.
         * materializedSchedules 로 생존분을 가리는 것도 안 된다(힌트라 누락·과다 가능).
         * 이 관문을 빠져나가는 재제출은 서버가 중복으로 거절한다.
         */
        if (isResubmitBlocked(resubmitGuardRef.current)) {
            createAlert(
                '등록 확인 필요',
                MUST_REVIEW_SCHEDULE_LIST_NOTICE,
                true,
                '목록 확인',
                () => goToScheduleList(refineDate.refineStartTime),
            );
            return;
        }

        submissionLockRef.current = true;
        createOneOnTwoSchedule.mutate(submission);
    };

    // 수업일정 등록 버튼 클릭이벤트
    const onClickCreateSchedule = () => {
        if (submissionLockRef.current) return;
        // 새 제출 시도는 앞 확인 창의 시도를 끊는다(어떤 검사에서 멈추든 앞 "그래도 잡기"가 남지 않게).
        closeConfirm();

        if (coach?.value === '') {
            createAlert('필수 입력', '담당 강사를 선택해주세요', true);
            return;
        }

        if (selectedMember.length === 0) {
            createAlert('필수 입력', '회원을 선택해주세요', true);
            return;
        }

        if (selectedMember.some(isScheduleMemberPickerReadOnly)) {
            createAlert('등록 불가', '탈퇴 회원은 새 수업에 등록할 수 없습니다', true);
            return;
        }

        if (
            sharedMembershipStaffBookingEnabled === true &&
            !areMembershipEntitlementsReady
        ) {
            const isEntitlementLoading =
                areMembershipEntitlementsLoading && !hasMembershipEntitlementsError;
            // 조회가 에러로 굳으면 react-query가 스스로 재조회하지 않는다.
            // '다시 시도' 안내가 실제로 동작하도록 재조회 버튼을 함께 준다.
            createAlert(
                '수강권 확인 필요',
                isEntitlementLoading
                    ? '공유 수강권 사용 권한을 확인하고 있습니다\n잠시 후 다시 시도해주세요'
                    : '공유 수강권 사용 권한을 확인하지 못했습니다\n네트워크 상태를 확인한 뒤 다시 조회해주세요',
                true,
                isEntitlementLoading ? '확인' : '다시 조회',
                isEntitlementLoading ? undefined : () => refetchMembershipEntitlements(),
            );
            return;
        }

        let findNullMembership = scheduleSelectedMember.findIndex(
            (item: any) => item?.memberships.length === 0,
        );

        if (findNullMembership !== -1) {
            // 공유 수강권이 목록에서 빠지는 두 경우(1:2 모드 / 담당 강사 불일치)를 구분해
            // 알린다. 알리지 않으면 직원이 진짜로 수강권이 없는 줄 알고 중복 발급하거나,
            // 공유가 고장났다고 오인한다.
            createAlert(
                '필수 입력',
                excludedSharedMembershipCounts[findNullMembership] > 0
                    ? '이 회원의 공유 수강권은 담당 강사의 수업에서만 등록할 수 있습니다\n수강권의 담당 강사를 선택하거나 개인 수강권을 사용해주세요'
                    : '수강권이 없는 회원이 존재합니다\n반드시 수강권을 만들고 일정을 만들어주세요',
                true
            );
            return;
        }

        // 2:1 수업일경우
        if (!isOneOnOneClass) {
            if (selectedMember.length !== 2) {
                createAlert('필수 입력', '1:2 수업일 경우 회원을 2명 선택해주세요', true);
                return;
            }
        }

        const concatStartTime = dayjs(concatDateAndTimeDayjs(selectedDay, startTime.value));
        const concatEndTime = dayjs(concatDateAndTimeDayjs(selectedDay, endTime.value));
        const refineStartTime = dayjs(concatStartTime).toISOString();
        const refineEndTime =
            dayjs(concatEndTime).format('HH:mm') === '00:00'
                ? dayjs(concatEndTime).add(1, 'day').toISOString()
                : dayjs(concatEndTime).toISOString();
        if (isSameDate(dayjs(refineStartTime), dayjs(refineEndTime))) {
            createAlert('입력 오류', '시작 시간과 완료 시간이 같습니다!\n다시 한번 확인해주세요!', '확인', () => {});
            return;
        }

        if (dayjs(refineStartTime).isAfter(dayjs(refineEndTime))) {
            createAlert(
                '입력 오류',
                '시작 시간이 완료 시간보다 늦을 수  없습니다!\n다시 한번 확인해주세요!',
                '확인',
                () => {}
            );
            return;
        }

        const refineDate: RefineDate = {
            refineStartTime: refineStartTime,
            refineEndTime: refineEndTime,
        };

        /**
         * 제출 방식 판정은 순수 모듈 한곳에서 한다.
         *
         * 예전에는 여기 조건식이 `||` 로 이어져 있었고 **두 번째 항에만 모드 가드가
         * 빠져 있었다.** 그래서 1:1 모드인데 두 번째 회원의 잔여가 0이면 1:2 등록이
         * 나갔다 — 1:1 화면은 selectedMember[0] 만 그리므로, 직원이 보지도 못한 회원
         * 때문에 일정이 2건 만들어진다. 공유 수강권과 무관한 원래 버그다.
         *
         * ⚠️ 이 화면은 모드 전환 라디오가 실제로 동작해 **1:2 가 정상 사용 경로**다.
         * 1:2 쪽 판정(첫째 소진·둘째 소진·둘 다 소진·둘 다 정상)은 한 글자도 바꾸지 않고,
         * 1:1 모드에서 1:2 가 새어 나가던 것만 닫는다.
         */
        const submitPlan = resolveScheduleSubmitPlan({
            isOneOnOneClass,
            oneOnOneMembership:
                scheduleSelectedMember[0]?.memberships[oneOnOneMemberShipIndex - 1],
            firstMembership:
                scheduleSelectedMember[0]?.memberships[firstMemberShipIndex - 1],
            secondMembership:
                scheduleSelectedMember[1]?.memberships[secondMemberShipIndex - 1],
        });

        // 공유 풀은 개인 수강권과 달리 잔여 회차를 초과해서 등록할 수 없다.
        // 날짜 경고보다 먼저 막는다 — "그래도 잡기"가 이 차단을 우회하면 안 된다.
        if (submitPlan.kind === 'SHARED_EXHAUSTED_BLOCKED') {
            createAlert(
                '등록 가능 회차 부족',
                '공유 수강권의 등록 가능 회차가 남아있지 않습니다\n기존 수업을 취소하거나 공유 수강권을 추가한 뒤 다시 시도해주세요',
                true,
            );
            return;
        }

        /**
         * 1:2 부분 성공 뒤의 재제출 차단을 확인 창 **앞에도** 둔다 — 막힐 제출에 날짜 경고부터
         * 띄우지 않기 위해서다. 전송 직전(submitOneOnTwoSchedule)의 같은 검사는 그대로 남긴다.
         * 이것은 추가이고 대체가 아니다.
         */
        if (submitPlan.submit === 'ONE_ON_TWO' && isResubmitBlocked(resubmitGuardRef.current)) {
            createAlert(
                '등록 확인 필요',
                MUST_REVIEW_SCHEDULE_LIST_NOTICE,
                true,
                '목록 확인',
                () => goToScheduleList(refineDate.refineStartTime),
            );
            return;
        }

        // 보낼 요청 내용을 여기서 한 번 만든다. 판정·확인 창·전송이 모두 이 객체를 쓴다.
        const submission: ScheduleSubmission =
            submitPlan.submit === 'ONE_ON_ONE'
                ? buildOneOnOneSubmission(refineDate)
                : buildOneOnTwoSubmission(refineDate);

        const findMemberName = (memberId: string) =>
            selectedMember.find((member: any) => member?.value === memberId)?.label || '';
        const dateWarningItems: ScheduleDateWarningItem[] =
            submission.target === 'ONE_ON_ONE'
                ? [
                      {
                          memberId: submission.memberId,
                          memberName: findMemberName(submission.memberId),
                          membershipId: submission.membership?.id,
                          membershipSharePoolId: submission.membership?.membershipSharePoolId,
                          times: [
                              {
                                  startTime: submission.refineDate.refineStartTime,
                                  endTime: submission.refineDate.refineEndTime,
                              },
                          ],
                      },
                  ]
                : submission.scheduleTimes.map((item) => ({
                      memberId: item.memberId,
                      memberName: findMemberName(item.memberId),
                      membershipId: item.membershipId,
                      membershipSharePoolId: item.membershipSharePoolId,
                      times: [{ startTime: item.startTime, endTime: item.endTime }],
                  }));
        // 1:2 는 두 회원 판정을 첫 쓰기 전에 모두 끝낸다(경고는 한 창에 모은다).
        const dateWarnings = findScheduleDateWarnings(
            dateWarningItems,
            scheduleSelectedMember.flatMap((member: any) => member?.memberships || []),
        );

        const send = async () => {
            // 날짜 경고까지 통과한 뒤, 보내기 직전에 정지 확인(취소·해제 실패면 보내지 않는다)
            const pauseTargets =
                submission.target === 'ONE_ON_ONE'
                    ? [
                          toPtPauseGateTarget(submission.membership, findMemberName(submission.memberId), [
                              submission.refineDate.refineStartTime,
                          ]),
                      ]
                    : [
                          toPtPauseGateTarget(
                              scheduleSelectedMember[0]?.memberships[firstMemberShipIndex - 1],
                              findMemberName(submission.scheduleTimes[0]?.memberId),
                              [submission.refineDate.refineStartTime],
                          ),
                          toPtPauseGateTarget(
                              scheduleSelectedMember[1]?.memberships[secondMemberShipIndex - 1],
                              findMemberName(submission.scheduleTimes[1]?.memberId),
                              [submission.refineDate.refineStartTime],
                          ),
                      ];
            if (submissionLockRef.current) return;
            // 스케줄 동작 잠금 재검사 — 정지 해제(첫 부수 쓰기)보다 **먼저** 본다(설계 §6-6)
            if (scheduleLock.requiresCheck() && !(await scheduleLock.ensure())) return;
            if (submissionLockRef.current) return;
            if (!(await pauseGate.confirmBeforeCreate(compactTargets(pauseTargets)))) return;
            if (submission.target === 'ONE_ON_ONE') {
                submitOneOnOneSchedule(submission);
                return;
            }
            submitOneOnTwoSchedule(submission);
        };

        const showMemberName = dateWarningItems.length > 1;
        // 공유 기간 밖은 차단(전송 0회), 개인만 걸리면 확인 창, 다 쓴 수강권과 겹치면 한 창.
        dispatchScheduleSubmitDecision({
            decision: resolveScheduleSubmitDecision(submitPlan, dateWarnings),
            createAlert,
            openConfirm,
            closeConfirm,
            send,
            buildConfirmContent: ({ exhausted, dateWarnings: personalWarnings }) =>
                buildScheduleConfirmAlertContent({
                    exhausted,
                    dateWarningMessage: formatScheduleDateWarningMessage(personalWarnings, {
                        showMemberName,
                    }),
                }),
            buildSharedDateBlockMessage: (sharedWarnings) =>
                formatSharedDateBlockMessage(sharedWarnings, { showMemberName }),
        });
    };    // 시간추가
    const onClickAddTime = (addNumber: number) => {
        if (
            dayjs(startTime.value).format('YYYY-MM-DD') ===
            dayjs(endTime.value).add(addNumber, 'minute').format('YYYY-MM-DD')
        ) {
            setEndTime({
                value: dayjs(endTime.value).add(addNumber, 'minute'),
                label: dayjs(dayjs(endTime.value).add(addNumber, 'minute')).format('A hh:mm'),
            });
        } else {
            createAlert('입력 오류', '추가 가능한 시간을 초과하였습니다', '확인', () => {});
        }
    };

    // 시간빼기
    const onClickSubTime = (subNumber: number) => {
        if (dayjs(startTime.value).isBefore(dayjs(endTime.value).subtract(subNumber, 'minute'))) {
            setEndTime({
                value: dayjs(endTime.value).subtract(subNumber, 'minute'),
                label: dayjs(dayjs(endTime.value).subtract(subNumber, 'minute')).format('A hh:mm'),
            });
        } else {
            createAlert('입력 오류', '감소 가능한 시간을 초과하였습니다', '확인', () => {});
        }
    };

    // 시작 시간이 변할 때 또는 수강권이 변할 때(회원 선택, 회원의 수강권 변경) endTime 정재
    useEffect(() => {
        if (startTime) {
            // 기본 수업 시간을 50분으로 설정
            let duration = 50;

            // 1대1 수업 또는 1대2 수업에 따른 Index 설정
            const membershipIndex = (isOneOnOneClass ? oneOnOneMemberShipIndex : firstMemberShipIndex) - 1;

            if (
                !!scheduleSelectedMember &&
                scheduleSelectedMember[0]?.memberships.length > 0 &&
                membershipIndex < scheduleSelectedMember[0]?.memberships.length
            ) {
                // 회원권에 있는 수업 시간으로 수업시간을 설정
                duration =
                    scheduleSelectedMember[0]?.memberships[oneOnOneMemberShipIndex - 1]?.period;
            }

            const endTime: Dayjs = dayjs(startTime.value).add(duration, 'minute');

            if (
                endTime.isAfter(dayjs(startTime.value).endOf('day')) ||
                isSameDate(endTime, dayjs(startTime.value).endOf('day'))
            ) {
                setEndTime({
                    value: dayjs().endOf('day').add(1, 'millisecond'),
                    label: dayjs().endOf('day').add(1, 'millisecond').format('A hh:mm'),
                });
            } else {
                setEndTime({ value: dayjs(endTime), label: dayjs(endTime).format('A hh:mm') });
            }
        }
    }, [startTime, scheduleSelectedMember, oneOnOneMemberShipIndex, firstMemberShipIndex]);

    // 회원이름 검색 정제
    useEffect(() => {
        if (memberList) {
            if (searchText !== '') {
                let filterMemberList = memberList.filter((e: any) => {
                    return (
                        e.label.includes(searchText) &&
                        !selectedMember.some((selected: any) => selected.value === e.value)
                    );
                });

                setFilteredMemberList(filterMemberList);
            } else {
                let filterMemberList = memberList.filter((e: any) => {
                    return !selectedMember.some((selected: any) => selected.value === e.value);
                });

                setFilteredMemberList(filterMemberList);
            }
        }
    }, [searchText, selectedMember]);

    // 시간 10분단위로 생성
    useEffect(() => {
        // 시간생성
        const generateTimeSlots = () => {
            const timeSlots = [];
            const start = dayjs().startOf('day').hour(0).minute(0);
            for (let i = 0; i < 144; i++) {
                // 24 hours * 6 (10분 간격)
                const time = start.add(i * 10, 'minute');
                const formattedTime = time.format('A hh:mm'); // 오전/오후 표시
                timeSlots.push({ value: time, label: formattedTime });
            }
            return timeSlots;
        };

        const timeSlots = generateTimeSlots();
        setTimeList(timeSlots);
    }, []);

    //  1:1수업 2:1 수업일시 input disable 로직
    useEffect(() => {
        if (isOneOnOneClass) {
            selectedMember && selectedMember.length === 1 ? setIsDisabledInput(true) : setIsDisabledInput(false);
        } else {
            selectedMember && selectedMember.length === 2 ? setIsDisabledInput(true) : setIsDisabledInput(false);
        }
    }, [selectedMember]);

    // 1:1수업 , 2:1 수업 버튼 클릭시 상태 초기화
    useEffect(() => {
        setSelectedMember([]);
        setSearchText('');
    }, [isOneOnOneClass]);

    // _memberId가 있을 경우 회원 리스트 조회
    useEffect(() => {
        if (_memberId && !_membershipId) {
            filteredMemberList.length > 0 &&
                filteredMemberList.forEach((member: any) => {
                    if (member.value === _memberId) {
                        setSelectedMember([member]);
                    }
                });
        }
    }, [_memberId, filteredMemberList]);

    // _membershipId가 있을 경우 회원 리스트 조회
    useEffect(() => {
        if (_membershipId) {
            filteredMemberList.length > 0 &&
                filteredMemberList.forEach((member: any) => {
                    if (member.value === _memberId) {
                        setSelectedMember([member]);
                        //  memberships 에서 _membershipId 와 같은 membership 의 index 를 찾아서 oneOnOneMemberShipIndex 로 설정
                        member.memberships.forEach((membership: any, index: number) => {
                            const membershipId = membership.id || membership._id || membership.membershipId;
                            if (membershipId === _membershipId) {
                                setOneOnOneMemberShipIndex(index + 1);
                            }
                        });
                    }
                });
        }
    }, [_membershipId, filteredMemberList]);

    // 공유 참여자의 수강권은 권한 조회 후 합쳐지므로, 딥링크 선택도 합쳐진 목록에서 보정한다.
    useEffect(() => {
        if (!_membershipId || scheduleSelectedMember.length === 0) return;
        scheduleSelectedMember[0]?.memberships?.forEach((membership: any, index: number) => {
            const membershipId = membership.id || membership._id || membership.membershipId;
            if (membershipId === _membershipId) setOneOnOneMemberShipIndex(index + 1);
        });
    }, [_membershipId, scheduleSelectedMember]);

    //

    return (
        <Col style={{ paddingLeft: 101, paddingTop: 56, paddingRight: 101, paddingBottom: 64 }}>
            {/* 스케줄 동작 잠금 — 저장 직전 재검사 카드(body 포털) */}
            {scheduleLock.gate}
            {/* 등록 직전 확인 창(화면 지역, 고정 위치 덮개) */}
            {confirmDialog}
            {pauseGate.modal}
            <Row style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <Row style={{ alignItems: 'center' }}>
                    <div
                        style={{ cursor: 'pointer', marginRight: 16, display: 'flex', alignItems: 'center' }}
                        onClick={() => {
                            if (returnPath) {
                                navigate(returnPath, {
                                    state: returnState,
                                    replace: true,
                                });
                                return;
                            }

                            navigate('/schedule', {
                                state: { calendarType, calendarSelectedDay, calendarScrollY },
                                replace: true,
                            });
                        }}
                    >
                        <SvgIcon name={'SvgLeftStrokeArrow'} size={20} stroke={colors.black} fill={colors.trans} />
                    </div>
                    <Subtitle1Bold style={{ color: colors.LAVEL_4 }}>{'수업일정 등록하기'}</Subtitle1Bold>
                </Row>

                <ColorSquareButton
                    isGradient={true}
                    customStyle={{ width: 136, height: 36 }}
                    text="수업일정 등록"
                    size="small"
                    isDisabled={createOneOnOneSchedule.isLoading || createOneOnTwoSchedule.isLoading}
                    callBack={onClickCreateSchedule}
                />
            </Row>

            <Row style={{ marginTop: 56 }}>
                <CreateOneScheduleLeft
                    coachOption={coachOption}
                    coach={coach}
                    handleCoachSelect={handleCoachSelect}
                    selectedDay={selectedDay}
                    setIsCalendar={setIsCalendar}
                    timeList={timeList}
                    startTime={startTime}
                    handleStartTimeSelect={handleStartTimeSelect}
                    endTime={endTime}
                    handleEndTimeSelect={handleEndTimeSelect}
                    onClickAddTime={onClickAddTime}
                    onClickSubTime={onClickSubTime}
                />
                <CreateOneScheduleRight
                    setIsOneOnOneClass={setIsOneOnOneClass}
                    isOneOnOneClass={isOneOnOneClass}
                    selectedMember={scheduleSelectedMember}
                    filteredMemberList={filteredMemberList}
                    handleMemberSelect={handleMemberSelect}
                    searchText={searchText}
                    onChangeText={onChangeText}
                    coach={coach}
                    isDisabledInput={isDisabledInput}
                    onClickDeleteSelectedMember={onClickDeleteSelectedMember}
                    onClickClassPassPage={onClickClassPassPage}
                    oneOnOneMemberShipIndex={oneOnOneMemberShipIndex}
                    setOneOnOneMemberShipIndex={setOneOnOneMemberShipIndex}
                    firstMemberShipIndex={firstMemberShipIndex}
                    setFirstMemberShipIndex={setFirstMemberShipIndex}
                    secondMemberShipIndex={secondMemberShipIndex}
                    setSecondMemberShipIndex={setSecondMemberShipIndex}
                    excludedSharedMembershipCounts={excludedSharedMembershipCounts}
                />
            </Row>

            {isCalendar && (
                <Calendar
                    selectedDay={selectedDay}
                    setSelectedDay={setSelectedDay}
                    setIsCalendar={setIsCalendar}
                    isPrevMonth={true}
                    isNextMonth={true}
                    style={{
                        width: 420,
                        height: 370,
                        borderRadius: 24,
                        backgroundColor: colors.WHITE_50,
                        boxShadow: '1px 1px 8px 0px #2B529D2E',
                        position: 'absolute',
                        zIndex: 1999,
                        marginTop: 340,
                    }}
                />
            )}

            {isModalMembershipCreateVisible && (
                <ModalMembershipCreate
                    setIsVisible={setIsModalMembershipCreateVisible}
                    onClickCreateNewMembership={() => setIsRegisterPassModal(true)}
                    memberName={selectedMemberName}
                    coachName={coach?.label}
                    coachId={coach?.value}
                    memberId={selectedMemberId}
                    createAlert={createAlert}
                    type={'schedule'}
                />
            )}

            {isRegisterPassModal && (
                <ModalRegisterPass
                    modalVisible={isRegisterPassModal}
                    setModalVisible={setIsRegisterPassModal}
                    createAlert={createAlert}
                />
            )}
        </Col>
    );
};

export default CreateOneSchedule;
